/**
 * PurchaseOrderCreateDialog — create or edit a DRAFT purchase order.
 *
 * Reuses `StockItemSearch` for line items (RM Inward's bill-form
 * pattern) and `VendorSearchSelect` for vendor. Branch / Billing
 * Location / Shipping Location are plain fixed-list <Select> dropdowns
 * per the Phase 2 spec.
 *
 * DRAFT-only edits — the parent gates open/close on `po.status`.
 * When editing (`initialPo` provided), the form pre-fills every field
 * and PUTs to `/api/purchase-orders/{id}` on save. When creating,
 * POSTs to `/api/purchase-orders`.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../lib/apiError';
import axios from 'axios';
import VendorSearchSelect from './VendorSearchSelect';
import StockItemSearch from './StockItemSearch';
import { PO_TAX_OPTIONS, resolveTaxOptionValue } from '../constants/poTaxOptions';

const API = process.env.REACT_APP_BACKEND_URL;

const PO_TYPES = ['RM', 'FG', 'JOBWORK', 'EXPENSE'];
const emptyLine = () => ({
  item_id: '',
  description: '',
  hsn_or_sac: '',
  quantity_ordered: 0,
  unit_of_measure: 'PCS',
  unit_price: 0,
});

export default function PurchaseOrderCreateDialog({
  open,
  onClose,
  onSaved,
  initialPo = null,
  branches = [],
  billingLocations = [],
  shippingLocations = [],
  settings = null,
}) {
  const isEdit = !!initialPo;
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tdsTaxes, setTdsTaxes] = useState([]);
  // Sep 2026 — job-work vendors are also valid shipping locations
  // (contract §1). Names only; backend resolves the address at read
  // time. Loaded once per dialog open, appended to the existing
  // branch-name shipping list.
  const [jobWorkVendors, setJobWorkVendors] = useState([]);

  useEffect(() => {
    if (!open) return;
    const token = localStorage.getItem('token');
    const auth = token ? { Authorization: `Bearer ${token}` } : {};
    axios
      .get(`${API}/api/tds-taxes?status=ACTIVE`, { headers: auth })
      .then((res) => setTdsTaxes(Array.isArray(res.data) ? res.data : []))
      .catch(() => setTdsTaxes([]));
    axios
      .get(`${API}/api/vendors?is_job_worker=true`, { headers: auth })
      .then((res) => setJobWorkVendors(Array.isArray(res.data) ? res.data : []))
      .catch(() => setJobWorkVendors([]));
  }, [open]);

  const buildInitial = useCallback(() => {
    // Sep 2026 — `_expand_po_locations` returns billing/shipping as
    // objects on read ({name, address} for branches, {kind, name,
    // address, gstin} for job-work vendors). The create-dialog form
    // binds these to shadcn <Select value=...> which expects a
    // string. Coerce back to the plain-name string on load so:
    //   • the Select renders correctly on Edit
    //   • the submit body carries a plain string (unchanged write
    //     contract — backend re-expands on the next read).
    // No change to what gets sent to the backend; write-side shape
    // is preserved.
    const _locStr = (v) => {
      if (v == null) return '';
      if (typeof v === 'string') return v;
      if (typeof v === 'object') return v.name || '';
      return String(v);
    };
    if (initialPo) {
      return {
        po_type: initialPo.po_type || 'RM',
        vendor_id: initialPo.vendor_id || '',
        vendor_name: initialPo.vendor_name || '',
        vendor_address: initialPo.vendor_address || null,
        branch_id: initialPo.branch_id || '',
        branch: initialPo.branch || '',
        billing_location: _locStr(initialPo.billing_location),
        shipping_location: _locStr(initialPo.shipping_location),
        payment_terms: initialPo.payment_terms || '',
        notes: initialPo.notes || '',
        tax_rate: initialPo.tax_rate ?? settings?.default_tax_rate ?? 18,
        tax_type: initialPo.tax_type ?? settings?.default_tax_type ?? 'IGST',
        withholding_rate: initialPo.withholding_rate ?? settings?.default_withholding_rate ?? 0.1,
        withholding_tds_id: initialPo.withholding_tds_id ?? settings?.default_withholding_tds_id ?? null,
        adjustment_amount: initialPo.adjustment_amount ?? 0,
        expected_delivery_date: initialPo.expected_delivery_date
          ? initialPo.expected_delivery_date.slice(0, 10)
          : '',
        lines: (initialPo.lines || []).map((l) => ({
          item_id: l.item_id,
          description: l.description || '',
          hsn_or_sac: l.hsn_or_sac || '',
          quantity_ordered: l.quantity_ordered || 0,
          unit_of_measure: l.unit_of_measure || 'PCS',
          unit_price: l.unit_price || 0,
        })),
      };
    }
    return {
      po_type: 'RM',
      vendor_id: '',
      vendor_name: '',
      vendor_address: null,
      branch_id: '',
      branch: '',
      billing_location: '',
      shipping_location: '',
      payment_terms: '',
      notes: settings?.default_notes || 'TDS on purchase of goods exceeding 50 lakh 0.1%',
      tax_rate: settings?.default_tax_rate ?? 18,
      tax_type: settings?.default_tax_type ?? 'IGST',
      withholding_rate: settings?.default_withholding_rate ?? 0.1,
      withholding_tds_id: settings?.default_withholding_tds_id ?? null,
      adjustment_amount: 0,
      expected_delivery_date: '',
      lines: [emptyLine()],
    };
  }, [initialPo, settings]);

  useEffect(() => {
    if (open) setForm(buildInitial());
  }, [open, buildInitial]);

  if (!form) return null;

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const setLine = (idx, patch) => {
    const lines = [...form.lines];
    lines[idx] = { ...lines[idx], ...patch };
    setForm((f) => ({ ...f, lines }));
  };
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, emptyLine()] }));
  const removeLine = (idx) => {
    if (form.lines.length === 1) {
      toast.error('A PO must have at least one line');
      return;
    }
    setForm((f) => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));
  };

  // Live totals preview (uses the same math as backend).
  const subtotal = form.lines.reduce(
    (s, l) => s + (parseFloat(l.quantity_ordered) || 0) * (parseFloat(l.unit_price) || 0),
    0,
  );
  const taxAmount = subtotal * ((parseFloat(form.tax_rate) || 0) / 100);
  const withhold = subtotal * ((parseFloat(form.withholding_rate) || 0) / 100);
  const adjust = parseFloat(form.adjustment_amount) || 0;
  const grand = subtotal + taxAmount - withhold + adjust;

  const handleSave = async () => {
    // Client-side validation mirroring backend AC9.
    if (!form.vendor_id) return toast.error('Vendor is required');
    if (!form.branch_id) return toast.error('Branch is required');
    if (!form.billing_location) return toast.error('Billing location is required');
    if (!form.shipping_location) return toast.error('Shipping location is required');
    if (form.lines.length === 0) return toast.error('At least one line is required');
    for (let i = 0; i < form.lines.length; i++) {
      const l = form.lines[i];
      if (!l.item_id || !l.description) return toast.error(`Line ${i + 1}: item and description required`);
      if (!(parseFloat(l.quantity_ordered) > 0)) return toast.error(`Line ${i + 1}: qty must be > 0`);
      if (parseFloat(l.unit_price) < 0) return toast.error(`Line ${i + 1}: unit price must be ≥ 0`);
    }

    setSaving(true);
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
    const body = {
      ...form,
      expected_delivery_date: form.expected_delivery_date
        ? new Date(form.expected_delivery_date).toISOString()
        : null,
      lines: form.lines.map((l) => ({
        item_id: l.item_id,
        description: l.description,
        hsn_or_sac: l.hsn_or_sac || '',
        quantity_ordered: parseFloat(l.quantity_ordered),
        unit_of_measure: l.unit_of_measure,
        unit_price: parseFloat(l.unit_price),
      })),
    };

    try {
      if (isEdit) {
        const res = await axios.put(`${API}/api/purchase-orders/${initialPo.id}`, body, { headers });
        toast.success('Purchase order updated');
        onSaved?.(res.data);
      } else {
        const res = await axios.post(`${API}/api/purchase-orders`, body, { headers });
        toast.success(`Purchase order created: ${res.data.po_number}`);
        onSaved?.(res.data);
      }
      onClose?.();
    } catch (err) {
      // Feb 2026 (BUG-PO-RAW-DETAIL-CRASH) — route through
      // `formatApiError` so a structured `detail` (e.g. the
      // `{code, message, field, ...}` payload the PO location
      // validator returns) is rendered as a readable string,
      // never handed raw to React (which crashes with "Objects
      // are not valid as a React child"). Regression guard:
      // `__tests__/poRawErrorDetailRegression.test.js`.
      toast.error(formatApiError(err, 'Save failed').message);
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="po-create-dialog">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? `Edit Purchase Order — ${initialPo?.po_number || ''}` : 'Create Purchase Order'}
          </DialogTitle>
        </DialogHeader>

        {/* Header fields */}
        <div className="grid grid-cols-2 gap-4 mt-2">
          <div>
            <Label>PO Type</Label>
            <Select value={form.po_type} onValueChange={(v) => set({ po_type: v })}>
              <SelectTrigger data-testid="po-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PO_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Expected Delivery</Label>
            <Input
              type="date"
              value={form.expected_delivery_date}
              onChange={(e) => set({ expected_delivery_date: e.target.value })}
              data-testid="po-expected-delivery"
            />
          </div>

          <div className="col-span-2">
            <Label>Vendor <span className="text-red-500">*</span></Label>
            <VendorSearchSelect
              value={form.vendor_id}
              onSelect={(v) =>
                set({
                  vendor_id: v.id || '',
                  vendor_name: v.name || '',
                  vendor_address: v.id
                    ? {
                        line1: v.address_line1 || v.address || '',
                        city: v.city || '',
                        state: v.state || '',
                        pincode: v.pincode || '',
                        country: v.country || 'India',
                        gstin: v.gstin || '',
                      }
                    : null,
                })
              }
              testId="po-vendor"
            />
          </div>

          <div>
            <Label>Branch <span className="text-red-500">*</span></Label>
            <Select
              value={form.branch_id}
              onValueChange={(v) => {
                const b = branches.find((x) => x.id === v || x.name === v);
                set({ branch_id: v, branch: b?.name || v });
              }}
            >
              <SelectTrigger data-testid="po-branch-select"><SelectValue placeholder="Select branch" /></SelectTrigger>
              <SelectContent>
                {branches.map((b) => (
                  <SelectItem key={b.id || b.name} value={b.id || b.name}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Payment Terms</Label>
            <Input value={form.payment_terms} onChange={(e) => set({ payment_terms: e.target.value })} data-testid="po-payment-terms" />
          </div>

          <div>
            <Label>Billing Location <span className="text-red-500">*</span></Label>
            <Select value={form.billing_location} onValueChange={(v) => set({ billing_location: v })}>
              <SelectTrigger data-testid="po-billing-select"><SelectValue placeholder="Select billing location" /></SelectTrigger>
              <SelectContent>
                {billingLocations.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Shipping Location <span className="text-red-500">*</span></Label>
            <Select value={form.shipping_location} onValueChange={(v) => set({ shipping_location: v })}>
              <SelectTrigger data-testid="po-shipping-select"><SelectValue placeholder="Select shipping location" /></SelectTrigger>
              <SelectContent>
                {shippingLocations.map((l) => <SelectItem key={`br:${l}`} value={l}>{l}</SelectItem>)}
                {jobWorkVendors.length > 0 && shippingLocations.length > 0 && (
                  <div className="px-2 py-1 text-xs text-zinc-500 border-t mt-1">Job-work vendors</div>
                )}
                {jobWorkVendors.map((v) => (
                  <SelectItem
                    key={`jw:${v.id || v.vendor_id || v.name}`}
                    value={v.name}
                    data-testid={`po-shipping-jw-${v.name}`}
                  >
                    {v.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Line items */}
        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold">Line Items</h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[220px]">Item</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="w-[100px]">HSN/SAC</TableHead>
                <TableHead className="w-[80px] text-right">Qty</TableHead>
                <TableHead className="w-[80px]">UOM</TableHead>
                <TableHead className="w-[100px] text-right">Rate</TableHead>
                <TableHead className="w-[110px] text-right">Amount</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {form.lines.map((line, idx) => {
                const amt = (parseFloat(line.quantity_ordered) || 0) * (parseFloat(line.unit_price) || 0);
                return (
                  <TableRow key={idx} data-testid={`po-line-${idx}`}>
                    <TableCell>
                      <StockItemSearch
                        branch={form.branch}
                        type={form.po_type === 'FG' ? 'FG' : 'RM'}
                        value={line.item_id}
                        onSelect={(item) =>
                          setLine(idx, {
                            item_id: item.item_id || '',
                            description: item.name || item.description || line.description,
                            unit_price: item.prefilled_rate ?? line.unit_price,
                            hsn_or_sac: item.hsn_code ?? line.hsn_or_sac,
                            unit_of_measure: item.unit || line.unit_of_measure,
                          })
                        }
                        placeholder="Search item…"
                        testId={`po-item-${idx}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={line.description}
                        onChange={(e) => setLine(idx, { description: e.target.value })}
                        data-testid={`po-desc-${idx}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={line.hsn_or_sac}
                        onChange={(e) => setLine(idx, { hsn_or_sac: e.target.value })}
                        data-testid={`po-hsn-${idx}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        value={line.quantity_ordered}
                        onChange={(e) => setLine(idx, { quantity_ordered: e.target.value })}
                        onWheel={(e) => e.currentTarget.blur()}
                        className="no-spinner text-right"
                        data-testid={`po-qty-${idx}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={line.unit_of_measure}
                        onChange={(e) => setLine(idx, { unit_of_measure: e.target.value })}
                        data-testid={`po-uom-${idx}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        step="0.01"
                        value={line.unit_price}
                        onChange={(e) => setLine(idx, { unit_price: e.target.value })}
                        onWheel={(e) => e.currentTarget.blur()}
                        className="no-spinner text-right"
                        data-testid={`po-price-${idx}`}
                      />
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {amt.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeLine(idx)}
                        data-testid={`po-remove-line-${idx}`}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {/* Add Line at bottom — matches RM Inward bill-form pattern */}
              <TableRow>
                <TableCell colSpan={8}>
                  <Button variant="outline" size="sm" onClick={addLine} data-testid="po-add-line">
                    <Plus className="h-4 w-4 mr-2" /> Add Line
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        {/* Rates + totals preview */}
        <div className="mt-6 grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <div>
              <Label>Tax Type &amp; Rate</Label>
              <Select
                value={resolveTaxOptionValue(form.tax_type, form.tax_rate)}
                onValueChange={(v) => {
                  const opt = PO_TAX_OPTIONS.find((o) => o.value === v);
                  if (!opt) return;
                  set({ tax_type: opt.tax_type, tax_rate: opt.rate });
                }}
              >
                <SelectTrigger data-testid="po-tax-rate">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PO_TAX_OPTIONS.map((o) => (
                    <SelectItem
                      key={o.value}
                      value={o.value}
                      data-testid={`po-tax-option-${o.value}`}
                    >
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>TDS Withholding</Label>
              <Select
                value={form.withholding_tds_id || 'NONE'}
                onValueChange={(v) => {
                  if (v === 'NONE') {
                    set({ withholding_tds_id: null, withholding_rate: 0 });
                    return;
                  }
                  const row = tdsTaxes.find((t) => t.id === v);
                  set({
                    withholding_tds_id: v,
                    withholding_rate: Number(row?.rate ?? 0),
                  });
                }}
              >
                <SelectTrigger data-testid="po-withholding-rate">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NONE" data-testid="po-tds-option-NONE">None (0%)</SelectItem>
                  {tdsTaxes.map((t) => (
                    <SelectItem
                      key={t.id}
                      value={t.id}
                      data-testid={`po-tds-option-${t.id}`}
                    >
                      {t.label || `${t.tax_name} @ ${t.rate}%`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Adjustment (₹)</Label>
              <Input
                type="number"
                step="0.01"
                value={form.adjustment_amount}
                onChange={(e) => set({ adjustment_amount: e.target.value })}
                onWheel={(e) => e.currentTarget.blur()}
                className="no-spinner"
                data-testid="po-adjustment"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => set({ notes: e.target.value })} data-testid="po-notes" />
            </div>
          </div>
          <div className="rounded-md border p-4 h-fit bg-zinc-50" data-testid="po-totals-preview">
            <div className="flex justify-between text-sm py-1">
              <span>Sub Total</span>
              <span className="font-mono">{subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            <div className="flex justify-between text-sm py-1">
              <span>{form.tax_type || (parseFloat(form.tax_rate) ? 'IGST' : 'TAX')}{Math.round(parseFloat(form.tax_rate) || 0)} ({form.tax_rate}%)</span>
              <span className="font-mono">{taxAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            <div className="flex justify-between text-sm py-1">
              <span>Amount Withheld ({form.withholding_rate}%)</span>
              <span className="font-mono text-red-600">-{withhold.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            <div className="flex justify-between text-sm py-1">
              <span>Adjustment</span>
              <span className="font-mono">{adjust.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            <div className="flex justify-between text-base font-bold py-2 border-t mt-2">
              <span>Total</span>
              <span className="font-mono" data-testid="po-total-preview">₹ {grand.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="po-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {isEdit ? 'Save Changes' : 'Create PO'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
